import React from "react";
import LegalPage from "../../components/LegalPage";

export default function AMLKYCPolicy() {
  return (
    <LegalPage
      testid="legal-amlkyc"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Anti-Money Laundering, Sanctions and Know Your Customer (AML/KYC) Policy"
      lastUpdated="14 May 2026 · Version 1.1 (Global)"
      intro="BlueRoam Ltd (“BlueRoam”, “we”, “us”, “our”) is committed to preventing money laundering, terrorist financing, sanctions evasion, fraud and other financial crime in connection with the sale and provision of eSIM and related connectivity products and services (the “Services”) to customers worldwide via blueroam.io and any related applications."
    >
      <h2>1. Introduction and Purpose</h2>
      <p>
        While BlueRoam is not at present a “relevant person” under the UK Money Laundering, Terrorist
        Financing and Transfer of Funds (Information on the Payer) Regulations 2017 (as amended) (the
        “MLRs”), we voluntarily adopt and apply controls modelled on:
      </p>
      <ul>
        <li>The MLRs and the UK Proceeds of Crime Act 2002 (“POCA”) and Terrorism Act 2000;</li>
        <li>EU Anti-Money Laundering Directives (4AMLD–6AMLD) and the EU AML Regulation;</li>
        <li>US Bank Secrecy Act standards and FinCEN guidance (to the extent relevant to our merchant activity);</li>
        <li>Financial Action Task Force (“FATF”) Recommendations.</li>
      </ul>
      <p>
        We screen against and comply with all applicable financial sanctions regimes, including those
        administered by the UK Office of Financial Sanctions Implementation (“OFSI”), the EU, the
        United Nations Security Council, the US Office of Foreign Assets Control (“OFAC”), and any
        other regime that has extraterritorial application to our business.
      </p>

      <h2>2. Scope</h2>
      <p>This Policy applies to:</p>
      <ul>
        <li>All customers (individuals, sole traders, partnerships or corporate entities) purchasing or using the Services, <strong>regardless of country of residence</strong>;</li>
        <li>All directors, officers, employees, contractors, agents and any third parties acting on BlueRoam’s behalf;</li>
        <li>All payment, marketing, distribution and reseller relationships entered into by BlueRoam.</li>
      </ul>

      <h2>3. Risk-Based Approach</h2>
      <p>
        BlueRoam adopts a <strong>risk-based approach</strong> proportionate to the size, nature and
        global reach of its business. We periodically assess and document the money laundering,
        terrorist financing, sanctions and fraud risks arising from:
      </p>
      <ul>
        <li>Customer types and jurisdictions of origin;</li>
        <li>Geographic markets in which the Services are offered or used;</li>
        <li>Products and delivery channels (online-only, instant-activation eSIM products are inherently higher-risk for fraud and lower-risk for cash-based money laundering);</li>
        <li>Payment methods accepted (card, digital wallet, alternative payment methods, crypto if introduced);</li>
        <li>Transaction sizes, patterns and velocity.</li>
      </ul>
      <p>
        Higher-risk situations attract enhanced controls; lower-risk situations may attract simplified
        measures, in each case documented in our internal risk register and reviewed at least annually.
      </p>

      <h2>4. Customer Due Diligence (CDD)</h2>
      <h3>4.1 Standard Due Diligence</h3>
      <p>For ordinary retail purchases of eSIM products at standard price points, we apply the following baseline measures:</p>
      <ul>
        <li>Verified email address and (where collected) mobile number;</li>
        <li>Payment authentication via the customer’s payment service provider, including Strong Customer Authentication (“SCA”) under the EU PSD2 / UK PSR 2017 regimes where applicable;</li>
        <li>IP address, device fingerprinting and geolocation data collected for fraud-prevention and risk-screening purposes;</li>
        <li>Screening of transactional and payment data against sanctions and fraud databases by our payment service providers and our own systems.</li>
      </ul>

      <h3>4.2 Enhanced Due Diligence (EDD)</h3>
      <p>Enhanced measures apply where:</p>
      <ul>
        <li>The customer or beneficial owner is identified as a Politically Exposed Person (“PEP”), a family member or a known close associate of a PEP;</li>
        <li>The customer, the IP address, the payment instrument or the destination eSIM country is in a higher-risk jurisdiction as identified by FATF, the UK Government, the EU, OFAC or our internal risk assessment;</li>
        <li>Transactions are unusual in size, frequency or structure (including suspected “structuring” or “smurfing”), or are inconsistent with the customer’s apparent profile;</li>
        <li>There are reasonable grounds to suspect that the customer is acting on behalf of an undisclosed third party;</li>
        <li>The transaction appears designed to obscure identity, ownership or destination of funds.</li>
      </ul>
      <p>EDD may include requesting government-issued ID, proof of address, source-of-funds information, additional payment verification, or declining the transaction.</p>

      <h3>4.3 Country-Specific eSIM/SIM Registration</h3>
      <p>
        Many destination countries require eSIMs to be registered with verified identity documentation
        under local telecoms law. The current non-exhaustive list includes:
      </p>
      <ul>
        <li><strong>Europe:</strong> Germany, Spain, Italy, Hungary, Slovakia, Türkiye;</li>
        <li><strong>Middle East:</strong> UAE, Saudi Arabia, Qatar, Oman, Bahrain, Egypt;</li>
        <li><strong>Asia:</strong> China, India, Singapore, Thailand, Vietnam, South Korea, Japan (in some cases), Indonesia, Pakistan, Bangladesh;</li>
        <li><strong>Africa:</strong> South Africa, Nigeria, Kenya, Tanzania, Ghana, Morocco;</li>
        <li><strong>Americas:</strong> Mexico, Peru, Argentina, Venezuela;</li>
        <li><strong>Oceania:</strong> in some carriers, Australia and New Zealand.</li>
      </ul>
      <p>
        For these destinations, additional KYC information (passport scan, full name, date of birth,
        nationality and sometimes a selfie or video verification) will be collected at the point of
        purchase or activation and transmitted to the relevant upstream network operator or
        aggregator. Customers who decline are unable to activate.
      </p>
      <p>The list above is <strong>indicative and subject to change</strong> as local laws evolve.</p>

      <h2>5. Sanctions Screening</h2>
      <p>BlueRoam screens customers, transactions and counterparties against:</p>
      <ul>
        <li>The <strong>UK Sanctions List</strong> (OFSI);</li>
        <li>The <strong>EU Consolidated List</strong> of persons, groups and entities subject to financial sanctions;</li>
        <li>The <strong>UN Security Council Consolidated Sanctions List</strong>;</li>
        <li>The <strong>US OFAC Specially Designated Nationals (SDN) List</strong> and sectoral sanctions lists, including SSI, FSE and 50% Rule analysis;</li>
        <li>Such other national or regional sanctions or watch lists as we may consider appropriate.</li>
      </ul>
      <p>BlueRoam will not knowingly provide Services:</p>
      <ul>
        <li>to any individual or entity designated under any applicable sanctions regime;</li>
        <li>in or to any <strong>comprehensively sanctioned jurisdiction</strong>, currently including (without limitation) <strong>Cuba, Iran, North Korea, Syria and the Russian-occupied regions of Ukraine</strong> (Crimea, Donetsk, Luhansk, Kherson, Zaporizhzhia);</li>
        <li>in a manner that would breach <strong>US secondary sanctions</strong> even where BlueRoam itself is not a US person.</li>
      </ul>
      <p>
        We apply geo-blocking at checkout and IP-level controls to enforce these restrictions. Any
        positive match results in immediate suspension of the transaction, freezing of associated
        funds where required by law, and reporting to the relevant competent authority.
      </p>

      <h2>6. Export Controls</h2>
      <p>
        eSIMs and related connectivity services are generally treated as services (rather than
        controlled goods) under UK and US export control regimes, but encryption-related licensing
        and country-specific restrictions may apply. We do not provide eSIMs for delivery or
        activation in jurisdictions where doing so would breach applicable export controls.
      </p>

      <h2>7. Suspicious Activity and Internal Reporting</h2>
      <p>
        All employees and contractors must report any knowledge or reasonable suspicion of money
        laundering, terrorist financing, sanctions breach or fraud to BlueRoam’s nominated officer
        (the “Nominated Officer”), being <strong>Andrew Folson, Compliance Lead</strong>, of{" "}
        <strong>Elpidas 14, 4529, Pyrgos Limassol, Cyprus</strong>, contactable at{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.
      </p>
      <p>The Nominated Officer is responsible for:</p>
      <ul>
        <li>Assessing internal reports;</li>
        <li>Filing Suspicious Activity Reports (“SARs”) with the UK National Crime Agency under POCA and the Terrorism Act 2000;</li>
        <li>Filing equivalent reports with foreign competent authorities where required (e.g., FinCEN SARs in the US, AUSTRAC SMRs in Australia, FINTRAC STRs in Canada) — recognising that such obligations only arise if BlueRoam falls within those regimes;</li>
        <li>Liaising with OFSI and other sanctions authorities in respect of suspected sanctions breaches;</li>
        <li>Maintaining a confidential register of all internal disclosures and external reports.</li>
      </ul>
      <p>
        Employees must not “tip off” customers or any third party that a SAR has been made or
        contemplated. Tipping off is a criminal offence in many jurisdictions (e.g., section 333A of
        POCA in the UK).
      </p>

      <h2>8. Record Keeping</h2>
      <p>
        BlueRoam retains records of customer identification, transaction history, risk assessments,
        internal disclosures, SARs and training for a minimum of <strong>five (5) years</strong> from
        the end of the customer relationship or completion of the relevant transaction. Records will
        be securely destroyed thereafter unless retention is required by law or for legitimate legal,
        accounting or regulatory purposes.
      </p>

      <h2>9. Fraud Prevention</h2>
      <p>In addition to AML controls, BlueRoam operates the following fraud-prevention measures:</p>
      <ul>
        <li>Velocity limits and transaction caps per customer, payment instrument, IP address and device;</li>
        <li>Manual review of flagged transactions;</li>
        <li>Third-party fraud-scoring tools;</li>
        <li>Co-operation with card schemes, payment processors and law enforcement in respect of chargebacks and disputed transactions.</li>
      </ul>
      <p>
        Confirmed fraudulent purchases are voided, refunded to the legitimate cardholder, and may be
        reported to Action Fraud (UK), the FBI IC3 (US), or other competent authorities as appropriate.
      </p>

      <h2>10. Training</h2>
      <p>
        All relevant personnel receive AML, sanctions and fraud-awareness training on induction,
        annually thereafter, and following any material change in applicable law, internal policy or
        risk profile.
      </p>

      <h2>11. Governance and Review</h2>
      <p>
        This Policy is owned by BlueRoam’s board of directors and is reviewed at least{" "}
        <strong>annually</strong>, and additionally upon any material change in law, regulation,
        business model or risk profile.
      </p>

      <h2>12. Customer Inquiries</h2>
      <p>
        Questions about this Policy or BlueRoam’s compliance practices:{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.
      </p>

      <p className="!mt-12 text-xs text-ink-500 italic">
        This Policy is an internal compliance document and a public-facing statement of standards.
        It does not create third-party rights or contractual obligations beyond those in BlueRoam’s
        Terms of Service.
      </p>
    </LegalPage>
  );
}
